import React, { useState, useMemo } from 'react'
import { Stack, Box, Text, TextInput, Button, Card } from '@sanity/ui'
import { AddIcon, ChevronDownIcon, ChevronRightIcon } from '@sanity/icons'

interface FieldNode {
  name: string
  path: string
  description?: string
  children?: FieldNode[]
  isLeaf?: boolean
}

interface FieldPickerProps {
  onInsertField: (fieldPath: string) => void
}

/**
 * Context structure based on listingContext.js extractListingContext output
 */
const FIELD_STRUCTURE: FieldNode[] = [
  {
    name: 'guide',
    path: 'guide',
    description: 'Buyer guide information',
    children: [
      {
        name: 'vehicle',
        path: 'guide.vehicle',
        description: 'Vehicle details',
        children: [
          { name: 'make', path: 'guide.vehicle.make', isLeaf: true, description: 'Vehicle make name' },
          { name: 'model', path: 'guide.vehicle.model', isLeaf: true, description: 'Vehicle model name' },
          { name: 'generation', path: 'guide.vehicle.generation', isLeaf: true, description: 'Vehicle generation name' },
          { name: 'generationSlug', path: 'guide.vehicle.generationSlug', isLeaf: true, description: 'Generation URL slug' },
        ],
      },
      { name: 'title', path: 'guide.title', isLeaf: true, description: 'Guide title' },
      { name: 'totalListings', path: 'guide.totalListings', isLeaf: true, description: 'Total listings count' },
      { name: 'fitmentTypeCount', path: 'guide.fitmentTypeCount', isLeaf: true, description: 'Number of fitment types' },
    ],
  },
  {
    name: 'listing',
    path: 'listing',
    description: 'Current listing details',
    children: [
      {
        name: 'wheelSize',
        path: 'listing.wheelSize',
        description: 'Wheel specifications',
        children: [
          {
            name: 'front',
            path: 'listing.wheelSize.front',
            children: [
              { name: 'diameter', path: 'listing.wheelSize.front.diameter', isLeaf: true },
              { name: 'width', path: 'listing.wheelSize.front.width', isLeaf: true },
              { name: 'offset', path: 'listing.wheelSize.front.offset', isLeaf: true },
              { name: 'alternativeOffset', path: 'listing.wheelSize.front.alternativeOffset', isLeaf: true },
            ],
          },
          {
            name: 'rear',
            path: 'listing.wheelSize.rear',
            children: [
              { name: 'diameter', path: 'listing.wheelSize.rear.diameter', isLeaf: true },
              { name: 'width', path: 'listing.wheelSize.rear.width', isLeaf: true },
              { name: 'offset', path: 'listing.wheelSize.rear.offset', isLeaf: true },
              { name: 'alternativeOffset', path: 'listing.wheelSize.rear.alternativeOffset', isLeaf: true },
            ],
          },
          { name: 'sameAsFront', path: 'listing.wheelSize.sameAsFront', isLeaf: true, description: 'Whether rear matches front' },
          { name: 'setupType', path: 'listing.wheelSize.setupType', isLeaf: true, description: 'Square, Staggered, etc.' },
        ],
      },
      {
        name: 'tireDetails',
        path: 'listing.tireDetails',
        description: 'Tire information',
        children: [
          { name: 'types', path: 'listing.tireDetails.types', isLeaf: true, description: 'Array of tire types' },
          {
            name: 'recommendedSize',
            path: 'listing.tireDetails.recommendedSize',
            children: [
              { name: 'front', path: 'listing.tireDetails.recommendedSize.front', isLeaf: true },
              { name: 'rear', path: 'listing.tireDetails.recommendedSize.rear', isLeaf: true },
              { name: 'sameAsFront', path: 'listing.tireDetails.recommendedSize.sameAsFront', isLeaf: true },
            ],
          },
          { name: 'alternatives', path: 'listing.tireDetails.alternatives', isLeaf: true, description: 'Alternative tire sizes array' },
          { name: 'alternativeCount', path: 'listing.tireDetails.alternativeCount', isLeaf: true },
          {
            name: 'tireModelsAvailable',
            path: 'listing.tireDetails.tireModelsAvailable',
            children: [
              { name: 'front', path: 'listing.tireDetails.tireModelsAvailable.front', isLeaf: true },
              { name: 'rear', path: 'listing.tireDetails.tireModelsAvailable.rear', isLeaf: true },
              { name: 'total', path: 'listing.tireDetails.tireModelsAvailable.total', isLeaf: true },
            ],
          },
        ],
      },
      {
        name: 'modifications',
        path: 'listing.modifications',
        description: 'Required modifications',
        children: [
          {
            name: 'spacers',
            path: 'listing.modifications.spacers',
            children: [
              { name: 'front', path: 'listing.modifications.spacers.front', isLeaf: true },
              { name: 'frontReason', path: 'listing.modifications.spacers.frontReason', isLeaf: true },
              { name: 'rear', path: 'listing.modifications.spacers.rear', isLeaf: true },
              { name: 'rearReason', path: 'listing.modifications.spacers.rearReason', isLeaf: true },
            ],
          },
          {
            name: 'cambers',
            path: 'listing.modifications.cambers',
            children: [
              { name: 'front', path: 'listing.modifications.cambers.front', isLeaf: true },
              { name: 'frontReason', path: 'listing.modifications.cambers.frontReason', isLeaf: true },
              { name: 'rear', path: 'listing.modifications.cambers.rear', isLeaf: true },
              { name: 'rearReason', path: 'listing.modifications.cambers.rearReason', isLeaf: true },
            ],
          },
          { name: 'hasSpacers', path: 'listing.modifications.hasSpacers', isLeaf: true },
          { name: 'hasCambers', path: 'listing.modifications.hasCambers', isLeaf: true },
          { name: 'requiresModifications', path: 'listing.modifications.requiresModifications', isLeaf: true },
          { name: 'summary', path: 'listing.modifications.summary', isLeaf: true, description: 'Human-readable summary' },
        ],
      },
      {
        name: 'scoring',
        path: 'listing.scoring',
        description: 'Scoring data',
        children: [
          { name: 'values', path: 'listing.scoring.values', isLeaf: true, description: 'Array of scoring criteria' },
          { name: 'total', path: 'listing.scoring.total', isLeaf: true, description: 'Total score' },
          { name: 'maxPossible', path: 'listing.scoring.maxPossible', isLeaf: true },
          { name: 'percentage', path: 'listing.scoring.percentage', isLeaf: true },
          {
            name: 'easeOfFit',
            path: 'listing.scoring.easeOfFit',
            children: [
              { name: 'score', path: 'listing.scoring.easeOfFit.score', isLeaf: true },
              { name: 'maxScore', path: 'listing.scoring.easeOfFit.maxScore', isLeaf: true },
              { name: 'totalPenalty', path: 'listing.scoring.easeOfFit.totalPenalty', isLeaf: true },
              { name: 'breakdown', path: 'listing.scoring.easeOfFit.breakdown', isLeaf: true },
              { name: 'rating', path: 'listing.scoring.easeOfFit.rating', isLeaf: true, description: 'Easy, Moderate, or Difficult' },
            ],
          },
        ],
      },
      { name: 'tags', path: 'listing.tags', isLeaf: true, description: 'Array of tags with id, name, penaltyScore' },
      { name: 'extraNotes', path: 'listing.extraNotes', isLeaf: true, description: 'Array of extra fitment notes' },
      { name: 'flag', path: 'listing.flag', isLeaf: true, description: 'recommended, staff-pick, most-popular, or null' },
      { name: 'userContext', path: 'listing.userContext', isLeaf: true, description: 'User-provided context for AI' },
    ],
  },
  {
    name: 'relative',
    path: 'relative',
    description: 'Relative positioning and comparisons',
    children: [
      {
        name: 'fitmentType',
        path: 'relative.fitmentType',
        children: [
          { name: 'name', path: 'relative.fitmentType.name', isLeaf: true },
          { name: 'shortName', path: 'relative.fitmentType.shortName', isLeaf: true },
          { name: 'listingCount', path: 'relative.fitmentType.listingCount', isLeaf: true },
        ],
      },
      {
        name: 'positionInType',
        path: 'relative.positionInType',
        children: [
          { name: 'index', path: 'relative.positionInType.index', isLeaf: true },
          { name: 'total', path: 'relative.positionInType.total', isLeaf: true },
          { name: 'percentile', path: 'relative.positionInType.percentile', isLeaf: true },
        ],
      },
      {
        name: 'positionInGuide',
        path: 'relative.positionInGuide',
        children: [
          { name: 'total', path: 'relative.positionInGuide.total', isLeaf: true },
          { name: 'percentile', path: 'relative.positionInGuide.percentile', isLeaf: true },
        ],
      },
      {
        name: 'scoreComparison',
        path: 'relative.scoreComparison',
        children: [
          { name: 'score', path: 'relative.scoreComparison.score', isLeaf: true },
          { name: 'averageInType', path: 'relative.scoreComparison.averageInType', isLeaf: true },
          { name: 'averageInGuide', path: 'relative.scoreComparison.averageInGuide', isLeaf: true },
          { name: 'aboveTypeAverage', path: 'relative.scoreComparison.aboveTypeAverage', isLeaf: true },
          { name: 'aboveGuideAverage', path: 'relative.scoreComparison.aboveGuideAverage', isLeaf: true },
          { name: 'percentileInType', path: 'relative.scoreComparison.percentileInType', isLeaf: true },
          { name: 'percentileInGuide', path: 'relative.scoreComparison.percentileInGuide', isLeaf: true },
          { name: 'assessment', path: 'relative.scoreComparison.assessment', isLeaf: true, description: 'Human-readable assessment' },
        ],
      },
      { name: 'uniqueAspects', path: 'relative.uniqueAspects', isLeaf: true, description: 'Array of unique aspects' },
    ],
  },
]

/**
 * Recursive tree node component
 */
function TreeNode({
  node,
  depth,
  expandedPaths,
  toggleExpanded,
  onInsertField,
  searchTerm,
}: {
  node: FieldNode
  depth: number
  expandedPaths: Set<string>
  toggleExpanded: (path: string) => void
  onInsertField: (fieldPath: string) => void
  searchTerm: string
}) {
  const isExpanded = expandedPaths.has(node.path)
  const hasChildren = node.children && node.children.length > 0

  // Check if this node or any children match the search
  const matchesSearch = (n: FieldNode): boolean => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    if (n.name.toLowerCase().includes(term)) return true
    if (n.description?.toLowerCase().includes(term)) return true
    if (n.children?.some(matchesSearch)) return true
    return false
  }

  if (!matchesSearch(node)) {
    return null
  }

  const handleToggle = () => {
    if (hasChildren) {
      toggleExpanded(node.path)
    }
  }

  const handleInsert = (e: React.MouseEvent) => {
    e.stopPropagation()
    onInsertField(`{{${node.path}}}`)
  }

  return (
    <Box>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          paddingLeft: `${depth * 16 + 8}px`,
          cursor: hasChildren ? 'pointer' : 'default',
          borderRadius: '4px',
          backgroundColor: 'transparent',
        }}
        onClick={handleToggle}
      >
        {hasChildren ? (
          <Box style={{ width: '16px', flexShrink: 0 }}>
            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </Box>
        ) : (
          <Box style={{ width: '16px', flexShrink: 0 }} />
        )}
        <Text size={1} style={{ flex: 1 }}>
          {node.name}
          {node.description && (
            <Text as="span" size={0} muted style={{ marginLeft: '8px' }}>
              - {node.description}
            </Text>
          )}
        </Text>
        {node.isLeaf && (
          <Button
            icon={AddIcon}
            mode="ghost"
            tone="primary"
            padding={1}
            onClick={handleInsert}
            title={`Insert {{${node.path}}}`}
            style={{ flexShrink: 0 }}
          />
        )}
      </Box>
      {hasChildren && isExpanded && (
        <Box>
          {node.children!.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              toggleExpanded={toggleExpanded}
              onInsertField={onInsertField}
              searchTerm={searchTerm}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

/**
 * FieldPicker component - displays available template fields in a searchable tree
 */
export function FieldPicker({ onInsertField }: FieldPickerProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    // Start with top-level nodes expanded
    new Set(['guide', 'listing', 'relative'])
  )

  const toggleExpanded = (path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }

  // Auto-expand all when searching
  const effectiveExpandedPaths = useMemo(() => {
    if (!searchTerm) return expandedPaths

    // Expand all paths when searching
    const allPaths = new Set<string>()
    const collectPaths = (nodes: FieldNode[]) => {
      nodes.forEach((node) => {
        allPaths.add(node.path)
        if (node.children) {
          collectPaths(node.children)
        }
      })
    }
    collectPaths(FIELD_STRUCTURE)
    return allPaths
  }, [searchTerm, expandedPaths])

  return (
    <Stack gap={3} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box>
        <TextInput
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
        />
      </Box>
      <Card
        padding={2}
        radius={2}
        style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#fafafa',
          border: '1px solid #eee',
        }}
      >
        <Stack gap={1}>
          {FIELD_STRUCTURE.map((node) => (
            <TreeNode
              key={node.path}
              node={node}
              depth={0}
              expandedPaths={effectiveExpandedPaths}
              toggleExpanded={toggleExpanded}
              onInsertField={onInsertField}
              searchTerm={searchTerm}
            />
          ))}
        </Stack>
      </Card>
      <Text size={0} muted>
        Click [+] to insert field at cursor position
      </Text>
    </Stack>
  )
}

export default FieldPicker
